// @ts-strict-ignore
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ProjectOptionDefLocalizationVO, ProjectOptionDefVO } from '@project/model/vo/project.vo';
import { StringUtils } from '@shared/util/string-utils';
import { CustomValidators } from '@shared/custom-validators';
import { ProjectOptionDefIds } from '@project/components/project/constants/project-option-def-ids';
import { ProjectOptionUtils } from '../../util/project-option.utils';

@Component({
  standalone: false,
  templateUrl: './edit-project-option-def-item.component.html',
  selector: 'app-edit-project-option-def-item',

})
export class EditProjectOptionDefItemComponent implements OnInit {

  @Input() editOption: ProjectOptionDefVO;
  @Input() userOptions: ProjectOptionDefVO[];
  @Input() langKey: string;
  @Input() displayName: string;
  @Output() public finish = new EventEmitter<boolean | undefined>();

  public defaultValueRequired: boolean = false;
  public form: FormGroup;
  public showRequiredFields: boolean = false;

  constructor(private fb: FormBuilder, private bsModalRef: BsModalRef, private alertHelper: AlertHelper) { }

  ngOnInit(): void {
    this.defaultValueRequired = this.editOption.identifier === ProjectOptionDefIds.PROJECT_VERSION;
    const maxAllowedValueLength: number = ProjectOptionUtils.getMaxAllowedValueLength(this.editOption.maxLength);
    this.form = this.fb.group({
      identifier: [this.editOption.identifier, this.composeValidators(1, 255, true)],
      name: [this.displayName, this.composeValidators(2, 255, true)],
      defaultValue: [this.editOption.defaultValue,
        this.composeValidators(0, maxAllowedValueLength, this.defaultValueRequired)],
      isVisible: [{
        value: this.editOption.isVisible,
        disabled: this.editOption.isRequired
      }]
    });
  }

  private composeValidators(minLength: number, maxLength: number, required: boolean): any {
    const minMaxValidator: ValidatorFn = CustomValidators.minMaxLength(minLength, maxLength,
      'changeProjectOptionDefPopup.error_tooShortError', 'changeProjectOptionDefPopup.error_tooLongError');
    return required ? Validators.compose([minMaxValidator, CustomValidators.trimRequired]) : minMaxValidator;
  }

  public checkControlValidity(controlName: string): boolean {
    const control = this.form.controls[controlName];
    return (control.invalid && control.touched) || (control.invalid && this.showRequiredFields);
  }

  private isSecondIdentifier(newIdentifier: string): boolean {
    for (const userOption of this.userOptions) {
      if (newIdentifier === userOption.identifier) {
        if (!this.editOption) {
          return true;
        } else {
          if (userOption.order !== this.editOption.order) {
            return true;
          }
        }
      }
    }
    return false;
  }

  public isProjectOptionWithFixedName(): boolean {
    return ProjectOptionUtils.isProjectOptionWithFixedName(this.editOption.identifier);
  }

  public close(success?: boolean): void {
    this.bsModalRef.hide();
    this.finish.emit(success);
  }

  public save(): void {
    if (!this.form.valid) {
      this.showRequiredFields = true;
      return;
    }

    const newIdentifier: string = this.form.controls.identifier.value;
    const newOptionName: string = this.form.controls.name.value;
    const newDefaultValue: string = this.form.controls.defaultValue.value;
    const newIsVisible: boolean = this.form.controls.isVisible.value || false;

    if (this.isSecondIdentifier(newIdentifier)) {
      this.alertHelper.showError(
        'changeProjectOptionDefPopup.error_alert_isDuplicate_title',
        'changeProjectOptionDefPopup.error_alert_isDuplicate_text',
        {translationParams: [newIdentifier]});
      return;
    }

    this.editOption.identifier = newIdentifier;
    this.editOption.isVisible = newIsVisible;
    if (this.editOption.identifier === ProjectOptionDefIds.PROJECT_VERSION) {
      this.editOption.defaultValue = StringUtils.trim(newDefaultValue);
    } else {
      this.editOption.defaultValue = newDefaultValue;
    }
    let localizationExists: boolean = false;
    for (const optionLocale of this.editOption.localizations) {
      if (optionLocale.locale === this.langKey) {
        optionLocale.value = newOptionName;
        localizationExists = true;
        break;
      }
    }
    if (!localizationExists) {
      const localization: ProjectOptionDefLocalizationVO = new ProjectOptionDefLocalizationVO();
      localization.locale = this.langKey;
      localization.value = newOptionName;
      this.editOption.localizations.push(localization);
    }
    this.close(true);
  }
}
